// Internal state to track changes before saving
let groupingsState = [];

// Constants for CSS classes (updated to reflect new CSS)
const ACTIVE_CLASS = "button-active";
const INACTIVE_CLASS = "button-inactive";

// ====================================================================
// 1. MODAL HTML CREATION
// ====================================================================

/**
 * Returns the complete HTML structure for the modal dialogue.
 * Classes updated to use minimal CSS.
 */
function getModalHTML() {
  return `
            <div id="split-test-modal" class=" modal-overlay hidden" aria-modal="true" role="dialog">
                <div class="modal-content-wrapper">
                    <div class="modal-header">
                        <h2 style="font-size: 1.25rem; font-weight: 800; color: #1f2937;">A/B Test Group Configuration</h2>
                        <p style="font-size: 0.875rem; color: #6b7280;">Select your active group for each test.</p>
                    </div>

                    <div id="modal-content" class="modal-body">
                        <!-- Dynamic content will be injected here -->
                    </div>

                    <div class="modal-footer">
                        <button onclick="closeModal()" class="btn cancel-button-footer">
                            Cancel
                        </button>
                        <button onclick="saveChanges()" id="save-button" class="btn save-button-footer">
                            Save and Reload Page
                        </button>
                    </div>
                </div>
            </div>
        `;
}

function getModalStyling() {
    return `
            #split-test-modal .main-card-container{max-width:500px;margin:auto;background:#fff}
            #split-test-modal .hidden{display:none!important}
            #split-test-modal.modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;z-index:999}
            #split-test-modal .modal-content-wrapper{background:#fff;width:100%;max-width:540px;display:flex;flex-direction:column;border-radius:8px}
            #split-test-modal .modal-header,
            #split-test-modal .modal-body,
            #split-test-modal .modal-footer{padding:10px}
            #split-test-modal .modal-body{overflow-y:auto;flex-grow:1}
            #split-test-modal .modal-footer{display:flex;justify-content:flex-end}
            #split-test-modal .test-row{display:flex;flex-direction:row;padding:5px;margin-bottom:5px}
            #split-test-modal .test-name-container{flex-grow:1}
            #split-test-modal .test-buttons{display:flex}
            #split-test-modal .btn{padding:5px 10px;cursor:pointer;border:1px solid #d3d3d3;border-radius:8px;margin:4px}
            #split-test-modal .button-active{background:#4f46e5;color:#fff}
            #split-test-modal .save-button-footer{background:#4f46e5;color:#fff;border:none}
            #split-test-modal .cancel-button-footer{background:#d3d3d3}
            #split-test-modal .code-key{font-family:monospace}
            `
}

// ====================================================================
// 2. DATA HANDLING
// ====================================================================

/**
 * Parses the flat string into the internal state array.
 * @param {string} str The input string (e.g., "test=C|other=A").
 * @returns {Array<{name: string, value: string}>}
 */
function parseGroupings(str) {
  if (!str) return [];
  return str.split("|").map((item) => {
    const parts = item.split("=");
    return {
      name: parts[0] || "",
      value: parts[1] ? parts[1].toUpperCase() : "C", // Default to Control
    };
  });
}

/**
 * Recreates the flat string from the internal state array.
 * @param {Array<{name: string, value: string}>} state The internal state.
 * @returns {string} The serialized string (e.g., "test=C|other=A").
 */
function serializeGroupings(state) {
  return state.map((item) => `${item.name}=${item.value}`).join("|");
}

// ====================================================================
// 3. UI RENDERING
// ====================================================================

/**
 * Renders the dynamic content inside the modal based on the current state.
 * Classes updated to use minimal CSS.
 */
function renderModalContent() {
  // We assume modal exists when this is called, so we find its content area
  const content = document.getElementById("modal-content");
  if (!content) return;

  content.innerHTML = ""; // Clear previous content

  groupingsState.forEach((test, index) => {
    const isControl = test.value === "C";
    const isAlternate = test.value === "A";

    // Generate HTML for a single test
    const testElement = document.createElement("div");
    // Use the new CSS class for the row layout
    testElement.className = "test-row";

    testElement.innerHTML = `
                <div class="test-name-container">
                    <h3 class="test-name">${test.name}</h3>
                </div>
                <div class="test-buttons">
                    <button id="control-${index}" data-index="${index}" data-value="C"
                            class="btn group-button ${isControl ? ACTIVE_CLASS : INACTIVE_CLASS}">
                        Control
                    </button>
                    <button id="alternate-${index}" data-index="${index}" data-value="A"
                            class="btn group-button ${isAlternate ? ACTIVE_CLASS : INACTIVE_CLASS}">
                        Alternate
                    </button>
                </div>
            `;
    content.appendChild(testElement);
  });

  // Attach event listeners to the newly created buttons
  document.querySelectorAll(".group-button").forEach((button) => {
    button.addEventListener("click", handleGroupSelection);
  });
}

/**
 * Handles the click event for Control/Alternate buttons.
 * Updates the state and re-renders the buttons for highlighting.
 * @param {Event} event The click event.
 */
function handleGroupSelection(event) {
  const button = event.currentTarget;
  const index = parseInt(button.getAttribute("data-index"));
  const newValue = button.getAttribute("data-value");

  // 1. Update the internal state
  if (groupingsState[index]) {
    groupingsState[index].value = newValue;
  }

  // 2. Update button highlighting without full re-render (more efficient)
  // Find the closest container element that holds the two buttons
  const buttonContainer = button.closest(".test-buttons");
  if (!buttonContainer) return;

  const controlButton = buttonContainer.querySelector('[data-value="C"]');
  const alternateButton = buttonContainer.querySelector('[data-value="A"]');

  // Helper function to update button classes
  const toggleClasses = (btn, isActive) => {
    if (isActive) {
      btn.classList.remove(INACTIVE_CLASS);
      btn.classList.add(ACTIVE_CLASS);
    } else {
      btn.classList.remove(ACTIVE_CLASS);
      btn.classList.add(INACTIVE_CLASS);
    }
  };

  // Apply highlighting based on the new selection
  if (newValue === "C") {
    toggleClasses(controlButton, true);
    toggleClasses(alternateButton, false);
  } else if (newValue === "A") {
    toggleClasses(controlButton, false);
    toggleClasses(alternateButton, true);
  }
}

// ====================================================================
// 4. MODAL CONTROL
// ====================================================================

/**
 * Opens the modal and loads the initial data. Creates the modal element if it doesn't exist.
 */
function openModal() {
  let modal = document.getElementById("split-test-modal");

  // Check if the modal exists. If not, create and append it.
  if (!modal) {
    const modalstyle = document.createElement("style");
    modalstyle.innerHTML = getModalStyling();
    document.body.appendChild(modalstyle);   

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = getModalHTML();
    // Use children[0] to get the first element child (the modal itself)
    modal = tempDiv.children[0];
    document.body.appendChild(modal);
  } else {
    modal.style.display = "flex";
  }

  document.body.style.overflow = "hidden"; // Prevent background scrolling

  // Load and parse the initial data only when opening
  console.log("Initial split test groupings:", window.split_test_groupings);
  groupingsState = parseGroupings(window.split_test_groupings);

  renderModalContent();
}

/**
 * Closes the modal.
 */
function closeModal() {
  const modal = document.getElementById("split-test-modal");
  if (modal) {
    modal.style.display = "none";
  }
  document.body.style.overflow = "";
}

// ====================================================================
// 5. KEYBOARD TRIGGER
// ====================================================================

// State for tracking key presses
const pressedKeys = { q: false, f: false };

document.addEventListener("keydown", (e) => {
  const key = e.key.toLowerCase();

  if (key === "q" || key === "f") {
    pressedKeys[key] = true;

    // Check if both 'Q' and 'F' are pressed
    if (pressedKeys["q"] && pressedKeys["f"]) {
      // Prevent default browser action if necessary (e.g., Q on some systems)
      e.preventDefault();
      openModal();
    }
  }
});

document.addEventListener("keyup", (e) => {
  const key = e.key.toLowerCase();
  if (key === "q" || key === "f") {
    pressedKeys[key] = false;
  }
});

// ====================================================================
// 6. SAVE ACTION
// ====================================================================

/**
 * Mocks the action of sending data to update a cart attribute.
 */
function updateCartAttribute(key, value) {
  fetch("/cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ attributes: { split_test_groupings: value } }),
  })
    .then((response) => {
      // Optionally check if the response was successful before reloading
      if (response.ok) {
        window.location.reload(); // Reload the current page
      } else {
        // Handle error response, e.g., log it or show a message
        console.error("Update failed with status:", response.status);
      }
    })
    .catch((error) => {
      // Handle network errors or issues with the fetch itself
      console.error("Fetch error:", error);
    });
}

/**
 * Saves the current state, updates the cart attribute, and reloads the page.
 */
function saveChanges() {
  const newGroupingsString = serializeGroupings(groupingsState);

  closeModal();

  updateCartAttribute("split_test_groupings", newGroupingsString);
}
